/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, Fragment, useRef } from "react";
import propTypes from "prop-types";
import classnames from "classnames";
import invariant from "invariant";
import Icon from "../icons/Icon";
import { useDataSort, sortData } from "./useDataSort";
import Pagination from "./Pagination";
import { guid, createDataAttributes } from "../../utilities/index";
import { CSSTransition } from "react-transition-group";
import "./Table.scss";

const toggleRow = (rowKey, toggle, selections, rowData) => {
  if (toggle) {
    selections[rowData[rowKey]] = rowData;
  } else {
    delete selections[rowData[rowKey]];
  }
};

const selectionsAsArray = (sortBy, direction, selections, sortCompare) =>
  sortData(Object.values(selections), sortBy, direction, sortCompare);

const preSelectedAsObject = (preSelectedRows, rowKey) => {
  const obj = {};
  if (preSelectedRows?.length)
    preSelectedRows.forEach(r => (obj[r[rowKey]] = r));
  return obj;
};

const arraysEqual = (arr1, arr2) => {
  // if the other array is a falsy value, return
  if (!arr1 || !arr2) return false;
  // compare lengths - can save a lot of time
  if (arr1.length !== arr2.length) return false;
  for (let i = 0, l = arr1.length; i < l; i++) {
    // Check if we have nested arrays
    if (arr1[i] instanceof Array && arr2[i] instanceof Array) {
      // recurse into the nested arrays
      if (!arr1[i].equals(arr2[i])) return false;
    } else if (arr1.includes(arr2[i])) {
      // Warning - two different object instances will never be equal: {x:20} != {x:20}
      return false;
    }
  }
  return true;
};

const usePrevious = value => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

const Table = ({
  activePage,
  className,
  columns,
  compact,
  data,
  dataAttrib,
  dataPaginationAttrib,
  dataWrapperAttrib,
  disablePageSizeChange,
  expandedContent,
  groupHeaderContents,
  groupHeaderRowElements,
  groupHeaderKey,
  id = guid(),
  initialDirection,
  initialSort,
  initialSortCompare,
  isMinimal,
  itemsPerPage,
  keepInView,
  noResultsMessage,
  onPageChange,
  onRowClick,
  onRowCollapse,
  onRowExpand,
  onSelectionsChange,
  preSelectedRows,
  rowKey,
  searchQuery = "",
  selectable,
  selectableSingle,
  selectAllButton,
  showPagination
}) => {
  const isSingleSelection =
    selectable && !!selectableSingle?.rowAriaLabelColumn;

  invariant(
    typeof rowKey === "string" && !!rowKey,
    "Prop 'rowKey' is required to determine row identity."
  );

  invariant(
    (isSingleSelection && !!selectableSingle?.rowAriaLabelColumn) ||
      !isSingleSelection,
    "When using `selectableSingle`, `rowAriaLabelColumn` is required."
  );

  const prevPreSelecrted = usePrevious(
    Object.keys(preSelectedRows || []) || []
  );

  const [pageSize, setPageSize] = useState(itemsPerPage);

  // if the app wants to change the pageSize, update the state if the prop changes
  useEffect(() => {
    setPageSize(itemsPerPage);
  }, [itemsPerPage]);

  const initialTotalPageCount =
    (data?.length || 0) > 0 ? Math.ceil(data.length / pageSize) : 0;

  // preSelectedRows is an array of objects (similar to the array that is returned in onSelectionsChange)
  // we have to process them into an object indexed with the rowKey to get them
  // to match up to the selected items
  const [selections, setSelections] = useState(
    preSelectedAsObject(preSelectedRows, rowKey)
  );

  // if preselected property changes with a new dataset, then update the selected
  // rows to match the new set... ignoring the previous selection
  useEffect(() => {
    if (
      !!preSelectedRows &&
      !arraysEqual(
        prevPreSelecrted,
        Object.keys(preSelectedAsObject(preSelectedRows, rowKey))
      )
    ) {
      setSelections(preSelectedAsObject(preSelectedRows, rowKey));
    }
  }, [preSelectedRows]);

  // console.log(selections, preSelectedRows);
  // Track if user has selected a row or toggled a page at least once
  // in order to avoid firing onSelectionsChange in some scenarios.
  const [hasSelected, setHasSelected] = useState(false);

  const [expanded, setExpanded] = useState(null);
  const [expandedLoaded, setExpandedLoaded] = useState();

  const clearExpandedRow = () => {
    if (expanded) setExpanded(null);
  };

  // if the data changes, close any expanded rows
  useEffect(() => {
    clearExpandedRow();
  }, [data]);

  const isExpandable = !!expandedContent;

  // pull out the 'searchable' properties from the columns to pass into the search
  const searchInColumns = columns.filter(c => c.searchable).map(c => c.key);

  const table = useDataSort({
    data,
    paginate: showPagination,
    itemsPerPage: pageSize,
    sortBy: initialSort || null,
    direction:
      (initialSort || initialSortCompare) && initialDirection
        ? initialDirection
        : null,
    sortCompare: initialSortCompare,
    activePage: !activePage
      ? 1
      : activePage > initialTotalPageCount // dissallow active page to be greater than the total count of pages
      ? initialTotalPageCount
      : activePage,
    searchQuery: searchQuery.trim(),
    searchInColumns:
      !!searchQuery && searchInColumns.length ? searchInColumns : null,
    onPagination: () => {
      // when goToPage is called, close the expanded row if it is set
      clearExpandedRow();
    }
  });

  const isRowSelected = rowKey => selections[rowKey];
  const numberOfSelections = Object.keys(selections)?.length ?? 0;
  const checkAllIcon =
    numberOfSelections > 0 &&
    table.data.every(rowData => selections[rowData[rowKey]])
      ? "check"
      : table.data.some(rowData => selections[rowData[rowKey]])
      ? "dash"
      : null;

  const classes = classnames("uikit__table", {
    uikit__table__selectable: selectable,
    uikit__table__compact: compact,
    uikit__table__noTh: columns.filter(c => !c.label).length === columns.length,
    uikit__table__expandable: isExpandable
  });

  // if activePage is set or changed on the component, then move to that page
  useEffect(() => {
    if (activePage) {
      const currentPageCount = table.pages || initialTotalPageCount;

      // this calculates if the requested page is in the range of available pages...
      // ... go to last page if requested page is too large
      const pageNo = !activePage
        ? 1
        : activePage > currentPageCount
        ? currentPageCount
        : activePage;

      table.goToPage(pageNo);
    }
    // this should only update when activePage changes, ignore 'table' changes
  }, [activePage]);

  // hasGroupedColumns is used in the row map below
  // to reduce the times showAllColumnsInThisRow is calculated if there are no grouped columns
  const [hasGroupedColumns, setHasGroupedColumns] = useState(false);
  useEffect(() => {
    setHasGroupedColumns(columns.some(col => col.group));
  }, [columns]);

  // Call onSelectionsChange whenenver selections or their sort order has changed
  // in order for consumer to have selections in same sort order as the table.
  // This is important in some scenarios, e.g. pdf concatenation.
  useEffect(() => {
    if (selectable && onSelectionsChange && hasSelected) {
      onSelectionsChange(
        selectionsAsArray(
          table.sortBy,
          table.direction,
          selections,
          table.initialSortCompare
        )
      );
    }
    // Disabling b/c onSelectionsChange from consumer can create infinite
    // loop if they do not useMemo / useCallback on the function.
  }, [hasSelected, selectable, selections, table.direction, table.sortBy]);

  const handlePageSizeChange = newSize => {
    // update the page size
    setPageSize(newSize);

    // send the callback of the new page size if the client
    if (onPageChange) onPageChange(1);

    // update the active page to try to keep the previously viewed page in the new range
    table.goToPage(1);
  };

  useEffect(() => {
    if (showPagination && onPageChange && activePage !== table.activePage) {
      onPageChange(table.activePage);
    }
  }, [showPagination, onPageChange, activePage, table.activePage]);

  const handleHeaderClick = (key, dataType, sortCompare) => {
    table.setSortBy(key, dataType, sortCompare);
    // set sort direction to ascending if it's a new column selected
    if (
      (key !== table.sortBy && !table.sortCompare) ||
      (table.sortCompare && sortCompare !== table.sortCompare)
    ) {
      table.setDirection("asc");
    }
  };

  const handleOnRowClick = rowData => () => {
    // don't do anything if the table doesn't allow selecting rows
    if (!selectable) return false;

    const nextSelections = isSingleSelection ? {} : { ...selections };
    const toggle = !selections[rowData[rowKey]];
    toggleRow(rowKey, toggle, nextSelections, rowData);
    setSelections(nextSelections);
    setHasSelected(true);

    if (onRowClick) {
      onRowClick({ row: rowData, selected: toggle });
    }
  };

  const handleOnToggleAll = () => {
    // don't do anything if the table doesn't allow selecting rows
    if (!selectable || isSingleSelection) return false;

    // Determine whether we're selecting or deselecting page of data
    const toggle = !table.data.every(rowData => !!selections[rowData[rowKey]]);

    const nextSelections = { ...selections };
    //Use page data because toggling selection is per page
    if (selectAllButton === "all") {
      data.forEach(rowData => {
        toggleRow(rowKey, toggle, nextSelections, rowData);
      });
    }

    // selectAllButton == "page"
    table.data.forEach(rowData => {
      toggleRow(rowKey, toggle, nextSelections, rowData);
    });
    setSelections(nextSelections);
    setHasSelected(true);
  };

  // rowCompare is used to help determine the repetive data in a column
  // used to compare current row with previous row to hide columns set as 'isGrouped'
  let rowCompare = undefined;

  // since group headers can reset the zebra striping on the rows
  // use a variable to toggle when iterating through the rows
  let rowColorToggle = false;

  const sortByLabel = columns.find(c => c.key === table.sortBy);
  const sortingInfo = sortByLabel
    ? `Sorting by ${sortByLabel.label}.`
    : "Using default sorting.";

  // eslint-disable-next-line no-unused-expressions
  if (sortByLabel) table.direction === "asc" ? "ascending" : "descending";

  return (
    <div
      className={classnames(className, "uikit__table__wrapper", {
        "uikit__table--minimal": isMinimal
      })}
      {...createDataAttributes(dataWrapperAttrib)}
    >
      <div className="uikit__table-container">
        <table
          id={`uikit_table_${id}`}
          className={classes}
          {...(isSingleSelection && {
            role: "radiogroup",
            "aria-label": selectableSingle.tableAriaLabel ?? ""
          })}
          {...createDataAttributes(dataAttrib)}
        >
          <thead>
            <tr>
              {selectable && !isSingleSelection && (
                <th className="uikit__table__checkbox">
                  <span>
                    <button
                      className="uikit__table__toggle-checked"
                      onClick={handleOnToggleAll}
                    >
                      {checkAllIcon === "check" && <Icon icon="farCheck" />}
                      {checkAllIcon === "dash" && (
                        <span className="uikit__table__checkbox-dash">-</span>
                      )}
                      <span className="uikit-sr-only">toggle checked</span>
                    </button>
                  </span>
                </th>
              )}

              {/* Header spacing. */}
              {isSingleSelection && (
                <th className="uikit__table__checkbox"></th>
              )}

              {isExpandable && <th>&nbsp;</th>}
              {columns.map((col, index) => {
                const colIsSortable = col.sortable || col?.sortCompare;
                const colSortActive =
                  col.key === table.sortBy ||
                  (col?.sortCompare && col?.sortCompare === table?.sortCompare);

                const HeaderElement = col.sortable ? "button" : "span";

                return (
                  <th
                    // eslint-disable-next-line react/no-array-index-key
                    key={
                      !!col.label && !!col.key
                        ? col.key + col.label
                        : !!col.label && !col.key
                        ? col.label
                        : !col.label && !!col.key
                        ? col.key
                        : index
                    }
                    className={classnames(col.className, {
                      uikit__table__sortable: col.sortable,
                      ["uikit__text-" + col.align]: !!col.align
                    })}
                    onClick={() => {
                      if (col.sortable) {
                        clearExpandedRow();

                        // on column click, send the column key and the dataType to the sorter
                        handleHeaderClick(
                          col.key,
                          col.dataType,
                          col.sortCompare
                        );
                      }
                    }}
                  >
                    <HeaderElement className="uikit__table__th">
                      <span>{col.label}</span>

                      {colIsSortable && (
                        <span
                          className={classnames("uikit__table__th-sort", {
                            "uikit__table__th-sort__active": colSortActive
                          })}
                        >
                          {(table.direction === "asc" && colSortActive) ||
                          (!!initialSort && !colSortActive) ? (
                            <Icon icon="farAngleDown" />
                          ) : null}
                          {table.direction === "desc" && (
                            <Icon icon="farAngleUp" />
                          )}
                        </span>
                      )}
                    </HeaderElement>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {table.data.length === 0 && (
              <tr>
                <td
                  className="uikit__table__no-results"
                  colSpan={columns.length}
                >
                  {noResultsMessage}
                </td>
              </tr>
            )}
            {table.data.map(row => {
              const isSelected = isRowSelected(row[rowKey]);
              const selectedClass = isSelected
                ? "uikit__table__selected"
                : null;

              const isNewGroup =
                rowCompare &&
                rowCompare[groupHeaderKey] !== row[groupHeaderKey];

              // We need to clear row comparison when new group based on groupHeaderKey
              // so that we don't compare column keys against the previous row in a different group
              if (isNewGroup) {
                rowCompare = undefined;
              }

              // show group header only if there is groupHeaderContents or groupHeaderRowElements
              // and the rowcompare is different
              const showGroupHeader =
                (groupHeaderContents || groupHeaderRowElements) &&
                (!rowCompare ||
                  rowCompare[groupHeaderKey] !== row[groupHeaderKey]);

              // used to set alternating row colors - used in TR below
              rowColorToggle = showGroupHeader ? false : !rowColorToggle;

              /*
              showAllColumnsInThisRow:
              look ahead into the columns data...
              show all of the columns in the row if any column is set to group
              has data different than the previous row
            */
              const showAllColumnsInThisRow =
                rowCompare &&
                hasGroupedColumns &&
                columns.some(col => {
                  return col.group && rowCompare[col.key] !== row[col.key];
                });

              // columnCount is used to set the colspan of the Group Header
              const columnCount = selectable
                ? columns.length + 1
                : columns.length;

              const rowClassName =
                !!row.className && typeof row.className === "string"
                  ? row.className
                  : "";

              const rowRender = (
                <Fragment key={row[rowKey]}>
                  {
                    // show the rowGroupHeader only if table has groupHeaderKey and is a new group

                    // TODO: should the content in the TD be more flexible?
                    groupHeaderKey && showGroupHeader && (
                      <tr
                        className={classnames(
                          "uikit__table__group-header",
                          rowClassName
                        )}
                      >
                        {groupHeaderRowElements ? (
                          <>{groupHeaderRowElements(row)}</>
                        ) : (
                          (typeof groupHeaderContents === "string") ?
                          (<td colSpan={columnCount}>{groupHeaderContents}</td>) :
                          (<td colSpan={isExpandable ? columnCount + 1 : columnCount}>{groupHeaderContents(row)}</td>)
                        )}
                      </tr>
                    )
                  }
                  <tr
                    onClick={selectable ? handleOnRowClick(row) : undefined}
                    role={
                      // makes selectable rows use role "button"... and is overridden below to be "radio" if it's a sinle select
                      selectable ? "button" : ""
                    }
                    className={classnames(selectedClass, rowClassName, {
                      uikit__odd: !rowColorToggle,
                      uikit__even: rowColorToggle
                    })}
                    {...(isSingleSelection && {
                      "aria-checked": !!isSelected,
                      "aria-labelledby": `${row[rowKey]}-${[
                        selectableSingle?.rowAriaLabelColumn
                      ]}`,
                      role: "radio",
                      tabIndex: "0"
                    })}
                  >
                    {
                      // if it's a selectable table, show the checkbox in the first column
                      selectable && (
                        <td className="uikit__table__checkbox">
                          <span>
                            <span className="uikit__chk__bg"></span>
                            {isSelected && <Icon icon="farCheck" />}
                          </span>
                        </td>
                      )
                    }
                    {isExpandable && (
                      <td className="uikit__table__expandable__icon-container">
                        <button
                          className="uikit__table__expandable__toggle"
                          onClick={e => {
                            if (isExpandable) {
                              e.stopPropagation();
                            }

                            if (expanded?.[rowKey] !== row?.[rowKey]) {
                              // expand if it's a new row
                              setExpanded(row);
                            } else {
                              setExpanded(null);
                            }
                          }}
                        >
                          <Icon
                            className="uikit__table__expandable__icon"
                            icon={
                              expanded?.[rowKey] === row?.[rowKey]
                                ? "farAngleUp"
                                : "farAngleDown"
                            }
                          />
                        </button>
                      </td>
                    )}
                    {
                      // Loop through columns and render the cell appropriately.
                      columns.map((col, index) => {
                        const cellData = col.element
                          ? col.element(row)
                          : row[col.key];
                        const className = classnames(col.className, {
                          ["uikit__text-" + col.align]: !!col.align,
                          "uikit__text-nowrap": col.nowrap
                        });

                        const showCellData =
                          !rowCompare || showAllColumnsInThisRow
                            ? // is it the first row? or a fresh row of data?
                              true
                            : // is the column set to be grouped?
                            col.group
                            ? // if so, only show it if it's not the same as the previous row
                              rowCompare[col.key] !== row[col.key]
                            : // show it if it's none of the above
                              true;

                        return (
                          // eslint-disable-next-line react/no-array-index-key
                          <td
                            className={className}
                            key={
                              !!col.label && !!col.key
                                ? col.key + col.label
                                : !!col.label && !col.key
                                ? col.label
                                : !col.label && !!col.key
                                ? col.key
                                : index
                            }
                            {...(isSingleSelection && {
                              id: `${row[rowKey]}-${col.key}`
                            })}
                          >
                            {showCellData && cellData}
                          </td>
                        );
                      })
                    }
                  </tr>
                  {isExpandable && (
                    <CSSTransition
                      in={expanded?.[rowKey] === row?.[rowKey]}
                      timeout={200}
                      classNames="transition"
                      unmountOnExit
                      onEntered={() => {
                        // fire off onRowExpand from the client if it exists
                        if (onRowExpand) onRowExpand(row);

                        // any row opened will update expandedLoaded state
                        setExpandedLoaded(expanded?.[rowKey]);
                      }}
                      onExited={() => {
                        // fire off onRowClose from the client if it exists
                        if (onRowCollapse) onRowCollapse(row);

                        // only set the expanded row to null if only
                        // the current row is being closed
                        if (expandedLoaded === row?.[rowKey])
                          setExpandedLoaded(null);
                      }}
                    >
                      <tr className="uikit__table__expandable__row">
                        <td
                          colSpan={columns.length + 1}
                          className="uikit__table__expandable__row__cell"
                        >
                          <div className="uikit__table__expandable__row__content">
                            {expandedContent({
                              row,
                              isShowing:
                                expandedLoaded &&
                                expandedLoaded === row?.[rowKey]
                            })}
                          </div>
                        </td>
                      </tr>
                    </CSSTransition>
                  )}
                </Fragment>
              );

              rowCompare = row;
              return rowRender;
            })}
          </tbody>
        </table>
        <span
          className="uikit-sr-only"
          aria-atomic="true"
          aria-live="assertive"
        >
          {sortingInfo}
        </span>
      </div>
      {showPagination && (
        <Pagination
          id={`uikit_table_pagination_${id}`}
          activePage={table.activePage}
          goToPage={table.goToPage}
          nextPage={table.nextPage}
          prevPage={table.prevPage}
          pages={table.pages}
          pageSize={pageSize}
          defaultPageSize={itemsPerPage}
          totalRows={table.rowsCount}
          changePageSize={handlePageSizeChange}
          disablePageSizeChange={disablePageSizeChange}
          keepInView={keepInView}
          isExpandable={isExpandable}
          dataAttrib={dataPaginationAttrib}
        />
      )}
    </div>
  );
};

const keyAndElementCheck = (props, propName, componentName) => {
  if (props.key === undefined && props.element === undefined) {
    return new Error(
      `One of 'key or 'element' is required by columns in the '${componentName}' component.`
    );
  }
  if (props.key && typeof props.key !== "string") {
    return new Error(
      `column 'key' field must be a string in the '${componentName}' component.`
    );
  }
};

Table.propTypes = {
  id: propTypes.string,
  rowKey: propTypes.string.isRequired,
  data: propTypes.array.isRequired,
  compact: propTypes.bool,
  selectable: propTypes.bool,
  selectableSingle: propTypes.shape({
    rowAriaLabelColumn: propTypes.string.isRequired,
    tableAriaLabel: propTypes.string
    // tableAriaLabelledBy: propTypes.string // TODO
  }),
  columns: propTypes.arrayOf(
    propTypes.shape({
      key: keyAndElementCheck,
      label: propTypes.string,
      className: propTypes.string,
      sortable: propTypes.bool,
      sortCompare: propTypes.func,
      dataType: propTypes.oneOf(["string", "number", "date"]),
      group: propTypes.bool,
      element: keyAndElementCheck,
      align: propTypes.oneOf(["left", "right", "center", "auto"]),
      nowrap: propTypes.bool
    })
  ).isRequired,
  showPagination: propTypes.bool,
  itemsPerPage: propTypes.number,
  onRowClick: propTypes.func,
  groupHeaderKey: propTypes.string,
  groupHeaderContents: propTypes.oneOfType([
    propTypes.string,
    propTypes.node,
    propTypes.element,
    propTypes.func
  ]),
  groupHeaderRowElements: propTypes.func,
  onSelectionsChange: propTypes.func,
  preSelectedRows: propTypes.array,
  className: propTypes.string,
  initialSort: propTypes.string,
  initialDirection: propTypes.oneOf(["asc", "desc"]),
  initialSortCompare: propTypes.func,
  noResultsMessage: propTypes.oneOfType([
    propTypes.string,
    propTypes.node,
    propTypes.func,
    propTypes.element
  ]),
  disablePageSizeChange: propTypes.bool,
  selectAllButton: propTypes.oneOf(["page", "all"]),
  keepInView: propTypes.bool,
  activePage: propTypes.number,
  onPageChange: propTypes.func,
  searchQuery: propTypes.string,
  expandedContent: propTypes.oneOfType([
    propTypes.node,
    propTypes.func,
    propTypes.element
  ]),
  onRowExpand: propTypes.func,
  onRowCollapse: propTypes.func,
  isMinimal: propTypes.bool,
  dataAttrib: propTypes.arrayOf(Object),
  dataWrapperAttrib: propTypes.arrayOf(Object),
  dataPaginationAttrib: propTypes.arrayOf(Object)
};

Table.defaultProps = {
  disablePageSizeChange: false,
  initialDirection: "asc",
  itemsPerPage: 15,
  keepInView: true,
  noResultsMessage: "No Results",
  selectableSingle: null,
  selectAllButton: "page"
};

export default Table;
